import { useCallback, useSyncExternalStore } from 'react';

export const SIDEBAR_MIN = 200;
export const SIDEBAR_MAX = 420;
export const SIDEBAR_DEFAULT = 248;
export const SIDEBAR_RAIL = 60;

interface SidebarState {
  width: number;
  collapsed: boolean;
}

const KEY = 'crewline-sidebar';
const listeners = new Set<() => void>();
let state: SidebarState | null = null;

const clamp = (w: number) => Math.round(Math.min(SIDEBAR_MAX, Math.max(SIDEBAR_MIN, w)));

function read(): SidebarState {
  if (state) return state;
  try {
    const raw = localStorage.getItem(KEY);
    const parsed = raw ? (JSON.parse(raw) as Partial<SidebarState>) : {};
    state = { width: clamp(typeof parsed.width === 'number' ? parsed.width : SIDEBAR_DEFAULT), collapsed: Boolean(parsed.collapsed) };
  } catch {
    state = { width: SIDEBAR_DEFAULT, collapsed: false };
  }
  return state;
}

function write(next: SidebarState): void {
  state = next;
  try {
    localStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    /* ignore */
  }
  for (const l of listeners) l();
}

function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

const SERVER: SidebarState = { width: SIDEBAR_DEFAULT, collapsed: false };

/** Persisted sidebar geometry (per device) shared by the shell, its resize handle, the `[` shortcut and the Account page. */
export function useSidebar() {
  const s = useSyncExternalStore(subscribe, read, () => SERVER);
  const setWidth = useCallback((w: number) => write({ ...read(), width: clamp(w), collapsed: false }), []);
  const setCollapsed = useCallback((collapsed: boolean) => write({ ...read(), collapsed }), []);
  const toggle = useCallback(() => write({ ...read(), collapsed: !read().collapsed }), []);
  const reset = useCallback(() => write({ width: SIDEBAR_DEFAULT, collapsed: false }), []);
  return { ...s, effectiveWidth: s.collapsed ? SIDEBAR_RAIL : s.width, setWidth, setCollapsed, toggle, reset };
}
